/*
 * File: Sidebar.tsx
 * Project: a-a
 * File Created: Tuesday, 22nd October 2024 1:21:43 am
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Thursday, 5th February 2026 10:43:48 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';

import zeplinColor from '../../../theme/Color';
import { AuthService } from '../../../service/auth.service';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import RoutePaths from '../../../routes/RoutePaths';
import SidebarData from './Sidebar-data';
import styles from './Sidebar.module.css';
import CustomModal from '../CustomModal/CustomModal';
import CustomCard from '../CustomCard/CustomCard';
import { LightTooltip } from '../CustomTooltip/CustomTooltip';
import CustomButton from '../CustomButton/CustomButton';
import { SlackService } from '../../../service/slack.service';
import { isSlug } from 'validator';
import queryString from 'query-string';
import CustomPopOver from '../CustomPopOver/CustomPopOver';
import { AsanaService } from '../../../service/asana.service';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchIntegrations } from '../../../store/slices/integrationSlice';
import { switchAdmin } from '../../../store/slices/dashboardSlice';

const drawerWidth = 240;

const Sidebar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { integrations, loading: integrationLoading } = useAppSelector(
    (state) => state.integration,
  );
  const { adminConsole: admin } = useAppSelector((state) => state.dashboard);
  const asanaToken = localStorage.getItem('asana-token') || '';
  // const IsSlackConnected = sessionStorage.getItem('isSlackConnected') === 'true';
  // const sfToken = sessionStorage.getItem('SFToken') || '';
  const captiv8 = sessionStorage.getItem('captiv8') || '';
  const token = sessionStorage.getItem('user-token') || '';
  const isMSLogin = sessionStorage.getItem('ms_login') || '';
  let accessToken: any = {};
  if (token) {
    accessToken = JSON.parse(token);
  }
  const [IsSlackConnected, setIsSlackConnected] = React.useState(
    sessionStorage.getItem('isSlackConnected') === 'true',
  );
  const [asanaConnected, setAsanaConnected] = React.useState(asanaToken ? true : false);
  const [sfToken, setSfToken] = React.useState(sessionStorage.getItem('SFToken') === 'true');
  React.useEffect(() => {
    const handleCustomStorageChange = (e: CustomEvent) => {
      console.log('Custom storage change detected:', e.detail);
      if (e.detail.key === 'isSlackConnected') {
        setIsSlackConnected(e.detail.value === 'true');
      }
      if (e.detail.key === 'SFToken') {
        setSfToken(e.detail.value === 'true');
      }
      if (e.detail.key === 'asana-token') {
        setAsanaConnected(e.detail.value);
      }
    };

    // Add event listener
    window.addEventListener('sessionStorageChange', handleCustomStorageChange as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener(
        'sessionStorageChange',
        handleCustomStorageChange as EventListener,
      );
    };
  }, []);

  React.useEffect(() => {
    dispatch(fetchIntegrations());
  }, [dispatch]);

  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [taskAnchorEl, setTaskAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [selectedTitle, setSelectedTitle] = React.useState<string>('');
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const envMode = process.env.REACT_APP_ENV === 'prod' ? 'prod' : 'dev';
  const integrationIconMap: Record<string, string> = {
    asana: '/assets/asana-logo.png',
    zoominfo: '/assets/zoominfo-logo.svg',
    salesforce: '/assets/salesforce.png',
    slack: '/assets/slack-nav-icon.svg',
    captiv8: '/assets/captiv8-icon.svg',
  };

  const integrationItems = React.useMemo(() => {
    const fixedIntegrations = ['asana', 'zoominfo'];
    return (integrations || [])
      .map((integration) => {
        const config = integration?.[envMode];
        const homeUrl = (config?.homeUrl || '').trim();
        return {
          title: integration?.name || 'Integration',
          path: homeUrl,
          icon: integration?.iconUrl || '/assets/client.svg',
          isEnabled: Boolean(config?.isEnabled),
        };
      })
      .filter(
        (item) =>
          item.isEnabled && item.path && !fixedIntegrations.includes(item.title.toLowerCase()),
      );
  }, [integrations, envMode]);
  const signOut = async () => {
    setLoading(true);
    try {
      const res = await AuthService.logout({
        accessToken: accessToken?.AccessToken,
        isMSLogin: isMSLogin ? true : false,
      });
      console.log(res);

      if (res?.microsoftLogoutUrl) {
        // const fullPath = '/' + res?.microsoftLogoutUrl;
        // sessionStorage.clear();
        // sessionStorage.setItem('logoutOutUrl', res?.microsoftLogoutUrl);
        window.location.href = res?.microsoftLogoutUrl;
      } else {
        // sessionStorage.clear();
        navigate(RoutePaths.Root);
      }
      sessionStorage.removeItem('loginExpiry');
      sessionStorage.removeItem('sf_path');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('user-name');
      sessionStorage.removeItem('user-token');
      sessionStorage.removeItem('asana-token');
      sessionStorage.removeItem('asana-cred');
      sessionStorage.removeItem('asana-expiry');
      sessionStorage.removeItem('currentTab');
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAsanaUnlink = async () => {
    try {
      setLoading(true);
      const asanaCredentials = localStorage.getItem('asana-cred') || '';
      const parsedCreds = asanaCredentials ? JSON.parse(asanaCredentials) : {};
      const res = await AsanaService.asanaLogout(
        { refresh_token: parsedCreds?.refresh_token },
        { 'asana-token': parsedCreds?.access_token },
      ); // Call the logout service
      localStorage.removeItem('asana-token');
      localStorage.removeItem('asana-expiry');
      localStorage.removeItem('asana-cred');
      navigate(RoutePaths.Asana);
      window.location.reload();
    } catch (err) {
      console.error('Error unlinking Asana:', err);
    } finally {
      setLoading(false);
    }
  };
  const handleSFUnlink = async () => {
    try {
      setLoading(true);
      const res = await SlackService.unlinkSlack({ unlinkSF: true }); // Assuming you use the same service or create a new one
      sessionStorage.setItem('SFToken', 'false');
      window.location.reload();
    } catch (err) {
      console.error('Error unlinking Salesforce:', err);
    } finally {
      setLoading(false);
    }
  };
  const handleUnlink = async () => {
    try {
      setLoading(true);
      const res = await SlackService.unlinkSlack({ unlinkSlack: true });
      sessionStorage.setItem('isSlackConnected', 'false');
      window.location.reload();
    } catch (err) {
      console.error('Error unlinking Slack:', err);
    } finally {
      setLoading(false);
    }
  };

  const openTask = Boolean(taskAnchorEl);
  const idTask = openTask ? 'simple-popover' : undefined;

  const handleMenuClick = () => {
    if (admin) {
      dispatch(switchAdmin());
    }
  };
  return (
    <Drawer
      sx={{
        width: ' 210px',
        flexShrink: 0,
        height: '100vh',
        '& .MuiDrawer-paper': {
          width: '210px',
          boxSizing: 'border-box',
          backgroundColor: zeplinColor.BasicBlack,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'scroll',
        },
      }}
      variant='permanent'
      anchor='left'
    >
      <Box sx={{ flexGrow: 1 }}>
        <Toolbar
          sx={{
            // height: { xs: '66px', xl: '96px' }, // Responsive height: 96px default, 120px for xl screens
            padding: { xs: '23px 16px', xl: '27px 24px' },
          }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: zeplinColor.BasicBlack,
            // height: '96px',
            // padding: '24px 0px',
          }}
        >
          <Link to={RoutePaths.Home}>
            <img
              // width={117}
              // height={30}
              // src={'/assets/white-logo-min.png'}
              width={130}
              height={20}
              src={'/assets/new-white-logo-min.png'}
              alt='logo'
              style={{ imageRendering: 'pixelated', filter: 'blur(0px)' }}
            />
          </Link>
        </Toolbar>
        <Divider />
        <List
          sx={{
            display: 'grid',
            rowGap: { xs: '4px', xl: '8px' },
            padding: { xs: '1rem 16px', xl: '1.5rem 20px' },
          }}
        >
          {SidebarData.main.map((val, index) => (
            <NavLink
              key={val.path}
              to={val.path}
              style={({ isActive }) => ({
                ...{ textDecoration: 'none' },
                ...(isActive
                  ? {
                      // color: zeplinColor.BasicBlack,
                      borderRadius: 14,
                      backgroundColor: zeplinColor.Neutral10,
                    }
                  : {}),
              })}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.activeNavLink}` : styles.navLink
              }
              onClick={() => {
                handleMenuClick();
                sessionStorage.setItem('currentTab', '0');
                sessionStorage.removeItem('dealSelectedFromOverview');
              }}
            >
              <ListItem
                key={val.path}
                disablePadding
                style={{
                  color: zeplinColor.Neutral1,
                  // width: '170px',
                  // margin: 'auto',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: '12px',
                  }}
                >
                  <ListItemButton
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '12px',
                      padding: '0px',
                      // justifyContent: 'space-between',
                      // alignItems: 'flex-start',
                    }}
                    disableRipple
                  >
                    <ListItemIcon style={{ minWidth: 0 }}>
                      <img width={28} height={28} src={val.icon} alt='logo' />
                    </ListItemIcon>
                    <Typography variant='responsiveBody3'>{val.title}</Typography>
                  </ListItemButton>
                </Box>
              </ListItem>
            </NavLink>
          ))}
        </List>
        <Divider />
        <List
          sx={{
            display: 'grid',
            rowGap: { xs: '4px', xl: '8px' },
            padding: { xs: '1rem 16px', xl: '1.5rem 20px' },
          }}
        >
          <Typography
            variant='responsiveLabel1'
            sx={{
              color: zeplinColor.Neutral8,
              padding: { xs: '0px 12px 4px 12px', xl: '12px' },
            }}
          >
            Integrations
          </Typography>
          {SidebarData.app.map((val) =>
            integrationLoading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100%',
                  backgroundColor: zeplinColor.Neutral4,
                }}
              />
            ) : (
              <NavLink
                key={val.path}
                to={val.path}
                style={({ isActive }) => ({
                  ...{ textDecoration: 'none' },
                  ...(isActive
                    ? {
                        borderRadius: 14,
                        backgroundColor: zeplinColor.Neutral10,
                      }
                    : {}),
                })}
                className={({ isActive }) =>
                  isActive ? `${styles.navLink} ${styles.activeNavLink}` : styles.navLink
                }
                onClick={() => {
                  handleMenuClick();
                  sessionStorage.setItem('currentTab', '0');
                  sessionStorage.removeItem('dealSelectedFromOverview');
                }}
              >
                <ListItem
                  key={val.path}
                  disablePadding
                  style={{
                    color: zeplinColor.Neutral1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      padding: '12px',
                    }}
                  >
                    <ListItemButton
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '12px',
                        padding: '0px',
                      }}
                      disableRipple
                    >
                      <ListItemIcon style={{ minWidth: 0 }}>
                        <img width={28} height={28} src={val.icon} alt='logo' />
                      </ListItemIcon>
                      <Typography variant='responsiveBody3'>{val.title}</Typography>
                    </ListItemButton>
                    <LightTooltip
                      title={<></>}
                      placement='bottom-start'
                      style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                    >
                      <Box
                        sx={{
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onClick={(e) => {
                          if (val?.title === 'ZoomInfo') {
                            return;
                          }
                          e.stopPropagation();
                          e.preventDefault();
                          setTaskAnchorEl(e.currentTarget);
                          setSelectedTitle(val.title);
                        }}
                      >
                        <Box
                          sx={{
                            width: '7px',
                            height: '7px',
                            borderRadius: '50%',
                            backgroundColor:
                              val?.title === 'ZoomInfo' ||
                              (val.title === 'Asana' && (asanaConnected || asanaToken))
                                ? zeplinColor.Success
                                : zeplinColor.AlertInfo,
                          }}
                        ></Box>
                      </Box>
                    </LightTooltip>
                  </Box>
                </ListItem>
              </NavLink>
            ),
          )}
          {integrationLoading
            ? Array.from({ length: 2 }).map((_, index) => (
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    width: '100%',
                    backgroundColor: zeplinColor.Neutral4,
                  }}
                />
              ))
            : integrationItems.map((val) => (
                <NavLink
                  key={val.path}
                  to={val.path}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={({ isActive }) => ({
                    ...{ textDecoration: 'none' },
                    ...(isActive
                      ? {
                          borderRadius: 14,
                          backgroundColor: zeplinColor.Neutral10,
                        }
                      : {}),
                  })}
                  className={({ isActive }) =>
                    isActive ? `${styles.navLink} ${styles.activeNavLink}` : styles.navLink
                  }
                  onClick={() => {
                    sessionStorage.setItem('currentTab', '0');
                    sessionStorage.removeItem('dealSelectedFromOverview');
                  }}
                >
                  <ListItem
                    key={val.path}
                    disablePadding
                    style={{
                      color: zeplinColor.Neutral1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        padding: '12px',
                      }}
                    >
                      <ListItemButton
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '12px',
                          padding: '0px',
                        }}
                        disableRipple
                      >
                        <ListItemIcon style={{ minWidth: 0 }}>
                          <img
                            width={28}
                            height={28}
                            src={val.icon}
                            alt='logo'
                            style={{ borderRadius: '6px' }}
                          />
                        </ListItemIcon>
                        <Typography variant='responsiveBody3'>{val.title}</Typography>
                      </ListItemButton>
                      <Box
                        sx={{
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img src='/assets/arrow-white.svg' alt='arrow' width={20} height={20} />
                      </Box>
                    </Box>
                  </ListItem>
                </NavLink>
              ))}
        </List>
        <Divider />
        <List
          sx={{
            display: 'grid',
            rowGap: { xs: '4px', xl: '8px' },
            padding: { xs: '1rem 16px', xl: '1.5rem 20px' },
          }}
        >
          <Typography
            variant='responsiveLabel1'
            sx={{
              color: zeplinColor.Neutral8,
              // width: '170px',
              // margin: 'auto',
              padding: { xs: '0px 12px 4px 12px', xl: '12px' },
              // textAlign: 'center',
            }}
          >
            Quick Links
          </Typography>
          {SidebarData.quickLinks.map((val, index) => (
            <Box
              key={val.path}
              // to={val.path}

              // rel='noopener noreferrer'
              sx={{
                ':hover': {
                  borderRadius: 4,
                  backgroundColor: '#1c1c1c',
                },
              }}
              // className={({ isActive }) =>
              //   isActive ? `${styles.navLink} ${styles.activeNavLink}` : styles.navLink
              // }
              onClick={(e: any) => {
                e.stopPropagation();
                e.preventDefault();
                if (val?.title !== 'Captiv8') {
                  setTaskAnchorEl(e.currentTarget);
                  setSelectedTitle(val.title);
                }
              }}
            >
              <ListItem
                key={val.path}
                disablePadding
                style={{
                  color: zeplinColor.Neutral1,
                  // width: '170px',
                  // margin: 'auto',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: '12px',
                  }}
                >
                  <ListItemButton
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '12px',
                      padding: '0px',
                      // justifyContent: 'space-between',
                      // alignItems: 'flex-start',
                    }}
                    disableRipple
                  >
                    <ListItemIcon style={{ minWidth: 0 }}>
                      <img width={28} height={28} src={val.icon} alt='logo' />
                    </ListItemIcon>
                    <Typography variant='responsiveBody3'>{val.title}</Typography>
                  </ListItemButton>
                  <LightTooltip
                    title={<></>}
                    placement='bottom-start'
                    style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                  >
                    <Box
                      sx={{
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (val?.title !== 'Captiv8') {
                          setTaskAnchorEl(e.currentTarget);
                          setSelectedTitle(val.title);
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: '7px',
                          height: '7px',

                          borderRadius: '50%',
                          backgroundColor:
                            (val.title === 'Salesforce' && sfToken) ||
                            val.title === 'Captiv8' ||
                            (val.title === 'Slack' && IsSlackConnected)
                              ? zeplinColor.Success
                              : zeplinColor.AlertInfo,
                        }}
                      ></Box>
                    </Box>
                  </LightTooltip>
                </Box>
              </ListItem>
            </Box>
          ))}
        </List>
      </Box>
      <Box>
        <Divider />
        <List
          sx={{
            display: 'grid',
            rowGap: { xs: '4px', xl: '8px' },
            padding: { xs: '1rem 16px', xl: '1.5rem 20px' },
            marginTop: 'auto',
          }}
        >
          {['Log Out'].map((text, index) => (
            <NavLink
              key={'Logout'}
              to={'#'}
              style={({ isActive }) => ({
                ...{ textDecoration: 'none' },
                ...(isActive
                  ? {
                      // color: zeplinColor.BasicBlack,
                      borderRadius: 14,
                      // backgroundColor: zeplinColor.Neutral10,
                    }
                  : {}),
              })}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.activeNavLink}` : styles.navLink
              }
              onClick={handleOpen}
            >
              <ListItem
                key={text}
                disablePadding
                style={{
                  color: zeplinColor.Neutral1,
                  // width: '170px',
                  // margin: 'auto',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: '12px',
                  }}
                >
                  <ListItemButton
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '12px',
                      padding: '0px',
                      // justifyContent: 'space-between',
                      // alignItems: 'flex-start',
                    }}
                    disableRipple
                  >
                    <ListItemIcon style={{ minWidth: 0 }}>
                      <img width={28} height={28} src={'/assets/logout-logo.svg'} alt='logo' />
                    </ListItemIcon>
                    <Typography variant='responsiveBody3'>{text}</Typography>
                  </ListItemButton>
                </Box>
              </ListItem>
            </NavLink>
          ))}
        </List>
      </Box>
      <CustomModal handleClose={handleClose} open={open}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            zIndex: 1000,
          }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: '24px',
              backgroundColor: zeplinColor.Neutral1,

              width: '312px',
              height: '183px',
              boxShadow: 'rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              // display: 'flex',
              // flexDirection: 'column',
              // justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                  Log Out?
                </Typography>
                <Typography variant='body3' color={zeplinColor.BasicBlack}>
                  Are you sure you want to log out?
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',

                  gap: '16px',
                }}
              >
                <Box onClick={handleClose}>
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Neutral1,
                      border: `1px solid ${zeplinColor.BasicBlack}`,
                      cursor: 'pointer',
                      // width: '312px',
                      // height: '183px',

                      // display: 'flex',
                      // flexDirection: 'column',
                      // justifyContent: 'space-between',
                    }}
                    aria-role='button'
                    aria-disable={loading}
                  >
                    <Typography variant='buttonMedium' color={zeplinColor.BasicBlack}>
                      Cancel
                    </Typography>
                  </CustomCard>
                </Box>
                <Box onClick={signOut}>
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.BasicBlack,
                      cursor: 'pointer',
                      // border: `1px solid ${zeplinColor.BasicBlack}`,
                      // width: '312px',
                      // height: '183px',

                      // display: 'flex',
                      // flexDirection: 'column',
                      // justifyContent: 'space-between',
                    }}
                    aria-role='button'
                    aria-disable={loading}
                  >
                    <Typography
                      variant='buttonMedium'
                      color={zeplinColor.Neutral1}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {loading ? 'Logging out' : 'Log Out'}
                    </Typography>
                  </CustomCard>
                </Box>
              </Box>
            </Box>
          </CustomCard>
          {/* {loading ? <CustomLoader /> : ''} */}
        </Box>
      </CustomModal>
      <CustomPopOver
        setAnchorEl={setTaskAnchorEl}
        anchorEl={taskAnchorEl}
        id={idTask}
        sx={{
          borderRadius: '14px',
          boxShadow: 'rgba(0, 0, 0, 0.15)',
          marginTop: '8px',

          // marginRight: '2rem',
          // marginLeft: '-4rem',
        }}
      >
        {' '}
        <Box
          sx={{
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            backgroundColor: zeplinColor.BasicBlack,
            width: '403px',
            gap: '16px',
            // width: '403px',
          }}
        >
          <Box
            sx={{
              padding: '8px 10px',
              borderRadius: '30px',
              backgroundColor:
                (selectedTitle === 'Asana' && (asanaConnected || asanaToken)) ||
                (selectedTitle === 'Salesforce' && sfToken) ||
                (selectedTitle === 'Slack' && IsSlackConnected)
                  ? zeplinColor?.Success
                  : zeplinColor?.AlertInfo,
              // width: '90px',
              width: 'fit-content',
            }}
          >
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              {(selectedTitle === 'Asana' && (asanaConnected || asanaToken)) ||
              (selectedTitle === 'Salesforce' && sfToken) ||
              (selectedTitle === 'Slack' && IsSlackConnected) ||
              (selectedTitle === 'Captiv8' && captiv8)
                ? 'Connected'
                : 'Not Connected'}
            </Typography>
          </Box>
          <Divider component='div' sx={{ borderColor: zeplinColor.Neutral10 }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              // width: '600px',
              gap: '16px',
              flexWrap: 'wrap',
            }}
          >
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              {(selectedTitle === 'Asana' && (asanaConnected || asanaToken)) ||
              (selectedTitle === 'Salesforce' && sfToken) ||
              (selectedTitle === 'Slack' && IsSlackConnected) ||
              (selectedTitle === 'Captiv8' && captiv8)
                ? `Disconnecting this integration will disable all related ${selectedTitle} services for Huddle. You can reconnect it at any time`
                : `Click Connect to integrate ${selectedTitle} with Huddle`}
            </Typography>
            {(selectedTitle === 'Asana' && (asanaConnected || asanaToken)) ||
            (selectedTitle === 'Salesforce' && sfToken) ||
            (selectedTitle === 'Slack' && IsSlackConnected) ||
            (selectedTitle === 'Captiv8' && captiv8) ? (
              <Box sx={{ width: '100%' }}>
                <CustomButton
                  disabled={false}
                  variant='outlined'
                  color='customSecondary'
                  style={{
                    color: zeplinColor.Neutral1,
                    // width: '103px',
                    // margin: 'auto',
                    borderRadius: '8px',
                    // height: 56,

                    borderWidth: 1,
                    borderColor: zeplinColor.Neutral1,
                    // backgroundColor: zeplinColor.Neutral1,
                    // display: 'flex',
                    // gap: '16px',
                    '&:hover': {
                      backgroundColor: loading ? zeplinColor.Neutral8 : zeplinColor.Neutral5,
                      color: loading ? zeplinColor.Neutral1 : zeplinColor.BasicBlack,
                    },
                    '&.MuiButton-root': {
                      padding: '6px 16px',
                    },
                  }}
                  textStyle={{ fontSize: '14px' }}
                  text={loading ? 'Disconnecting...' : 'Disconnect'}
                  icon={''}
                  onClick={() => {
                    selectedTitle === 'Salesforce'
                      ? handleSFUnlink()
                      : selectedTitle === 'Slack'
                        ? handleUnlink()
                        : handleAsanaUnlink();
                  }}
                  // tabindex={5}
                />
              </Box>
            ) : (
              <Link
                to={
                  selectedTitle === 'Asana' && process.env.REACT_APP_ASANA_LOGIN
                    ? process.env.REACT_APP_ASANA_LOGIN
                    : selectedTitle === 'Salesforce'
                      ? `https://${process.env.REACT_APP_SALESFORCE_URI}/services/oauth2/authorize?client_id=${process.env.REACT_APP_CLIENT_SALESFORCE}&redirect_uri=${process.env.REACT_APP_REDIRECT}&response_type=code`
                      : `https://slack.com/oauth/v2/authorize?${queryString.stringify({
                          client_id: '1547360932450.9088642262659',
                          // scope: 'channels:manage,channels:read,channels:history,groups:read,groups:write,groups:history,im:read,im:history,mpim:read,mpim:history',
                          user_scope:
                            'users:read,channels:read,channels:history,groups:read,groups:write,chat:write,groups:history,im:read,im:history,mpim:read,mpim:history',
                          redirect_uri: `${process.env.REACT_APP_REDIRECT}`,
                        })}`
                }
                // target='_blank'
                // rel='noopener noreferrer'

                onClick={() => {
                  // Set session storage flag before redirecting
                  selectedTitle === 'Slack' && sessionStorage.setItem('slackConnecting', 'true');
                }}
                style={{ textDecoration: 'none', width: '100%' }}
              >
                <CustomButton
                  disabled={false}
                  variant='outlined'
                  color='customSecondary'
                  style={{
                    color: zeplinColor.Neutral1,
                    // width: '103px',
                    // margin: 'auto',
                    borderRadius: '8px',
                    // height: 56,
                    borderWidth: 1,
                    borderColor: zeplinColor.Neutral1,
                    // backgroundColor: zeplinColor.Neutral1,
                    // display: 'flex',
                    // gap: '16px',
                    '&:hover': {
                      backgroundColor: loading ? zeplinColor.Neutral8 : zeplinColor.Neutral5,
                      color: loading ? zeplinColor.Neutral1 : zeplinColor.BasicBlack,
                    },
                    '&.MuiButton-root': {
                      padding: '6px 16px',
                    },
                  }}
                  textStyle={{ fontSize: '14px' }}
                  text={loading ? 'Connecting...' : 'Connect'}
                  icon={''}
                  onClick={() => {}}
                  // tabindex={5}
                />
              </Link>
            )}
          </Box>
        </Box>
      </CustomPopOver>
    </Drawer>
  );
};

export default Sidebar;
